/**
 * API error model (T023, C4).
 *
 * Mirrors `crates/rm-ui/src/api.rs:13-143` VERBATIM: the backend emits ~16 error
 * codes in `{ error, message? }`, but the UI collapses them to 9 `ApiErrorCode`
 * variants with Polish messages (FR-017). Do not expand this set — it is the
 * parity contract. Polish strings are copied verbatim (no diacritics, matching
 * the Dioxus UI).
 *
 * Refresh-once is keyed on HTTP 401 (status), NOT on a code — 403 maps to
 * `Unauthorized` for display only (see http.ts).
 */

export type ApiErrorCode =
  | 'InvalidCredentials'
  | 'EmailNotVerified'
  | 'EmailAlreadyExists'
  | 'ValidationError'
  | 'RateLimitExceeded'
  | 'NotFound'
  | 'Unauthorized'
  | 'BetaAccessRequired'
  | 'Unknown'

/** Default Polish message per code (verbatim from rm-ui `from_response`/`from_status`). */
export const mapErrorCode = {
  InvalidCredentials: 'Nieprawidlowy email lub haslo',
  EmailNotVerified: 'Najpierw zweryfikuj swoj adres email',
  EmailAlreadyExists: 'Konto z tym adresem email juz istnieje',
  ValidationError: 'Sprawdz poprawnosc danych',
  RateLimitExceeded: 'Zbyt wiele prob. Sprobuj ponownie za chwile',
  NotFound: 'Nie znaleziono',
  Unauthorized: 'Sesja wygasla. Zaloguj sie ponownie',
  BetaAccessRequired: 'Zamknieta beta — Twoje konto nie ma jeszcze dostepu',
  Unknown: 'Wystapil nieoczekiwany blad',
} as const satisfies Record<ApiErrorCode, string>

export class ApiError extends Error {
  readonly code: ApiErrorCode
  /** HTTP status (0 for network/transport failures). */
  readonly status: number
  /**
   * Raw server-provided reason (JSON `message` or a non-JSON body such as
   * axum's deserialize-rejection text), when present. `message` stays the
   * user-facing Polish string; `detail` is the untranslated diagnostic that
   * forms can surface for actionable errors and dev logs read for debugging.
   */
  readonly detail?: string
  /** Exact backend error discriminator, independent from the collapsed UI code. */
  readonly serverCode?: string

  constructor(
    code: ApiErrorCode,
    message: string,
    status: number,
    detail?: string,
    serverCode?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.detail = detail
    this.serverCode = serverCode
  }
}

/** Substring EN→PL translation of backend `validation_error` messages. */
function translateValidation(msg: string): string {
  if (msg.includes('email is required')) return 'Podaj adres email'
  if (msg.includes('invalid email')) return 'Nieprawidlowy format email'
  if (msg.includes('password must be at least'))
    return 'Haslo musi miec minimum 8 znakow'
  if (msg.includes('password must be at most')) return 'Haslo jest zbyt dlugie'
  if (msg.includes('name is required')) return 'Podaj imie'
  // RM-117 job_spec (submit) validation — surface the offending field.
  if (msg.includes('view_id')) return 'Nieprawidlowy view_id'
  if (msg.includes('target_size')) return 'Nieprawidlowa docelowa szerokosc'
  if (msg.includes('unknown variant'))
    return 'Serwer nie rozpoznaje tego typu zadania (zaktualizuj/odbuduj backend)'
  return msg
}

/**
 * Fallback by HTTP status — verbatim `ApiError::from_status`. 403 → Unauthorized.
 * `detail` carries an untranslated server reason (e.g. an axum deserialize
 * rejection body) so callers/dev logs keep the actionable text.
 */
export function fromStatus(
  status: number,
  detail?: string,
  serverCode?: string,
): ApiError {
  switch (status) {
    case 401:
      return new ApiError(
        'Unauthorized',
        'Sesja wygasla. Zaloguj sie ponownie',
        401,
        detail,
        serverCode,
      )
    case 403:
      return new ApiError(
        'Unauthorized',
        'Brak dostepu',
        403,
        detail,
        serverCode,
      )
    case 404:
      return new ApiError('NotFound', 'Nie znaleziono', 404, detail, serverCode)
    case 422:
      return new ApiError(
        'ValidationError',
        'Sprawdz poprawnosc danych',
        422,
        detail,
        serverCode,
      )
    case 429:
      return new ApiError(
        'RateLimitExceeded',
        'Zbyt wiele prob. Sprobuj ponownie za chwile',
        429,
        detail,
        serverCode,
      )
    default:
      if (status >= 500 && status <= 599) {
        return new ApiError(
          'Unknown',
          'Blad serwera. Sprobuj ponownie pozniej',
          status,
          detail,
          serverCode,
        )
      }
      return new ApiError(
        'Unknown',
        `Wystapil nieoczekiwany blad (kod ${status})`,
        status,
        detail,
        serverCode,
      )
  }
}

interface ErrorBody {
  error?: string
  message?: string
}

/**
 * Parse a backend error response into an `ApiError` — verbatim
 * `ApiError::from_response`. `bodyText` is the raw response body (may be empty
 * or non-JSON). `onUnmapped` lets http.ts dev-log an unrecognized code (W6).
 */
export function fromResponse(
  status: number,
  bodyText: string,
  onUnmapped?: (code: string) => void,
): ApiError {
  let parsed: ErrorBody | null = null
  try {
    parsed = JSON.parse(bodyText) as ErrorBody
  } catch {
    parsed = null
  }
  const code = parsed?.error ?? ''
  const serverMessage = parsed?.message ?? ''

  switch (code) {
    case 'invalid_credentials':
      return new ApiError(
        'InvalidCredentials',
        mapErrorCode.InvalidCredentials,
        status,
        undefined,
        code,
      )
    case 'email_not_verified':
      return new ApiError(
        'EmailNotVerified',
        mapErrorCode.EmailNotVerified,
        status,
        undefined,
        code,
      )
    case 'email_already_exists':
      return new ApiError(
        'EmailAlreadyExists',
        mapErrorCode.EmailAlreadyExists,
        status,
        undefined,
        code,
      )
    case 'validation_error':
      return new ApiError(
        'ValidationError',
        serverMessage === ''
          ? mapErrorCode.ValidationError
          : translateValidation(serverMessage),
        status,
        serverMessage === '' ? undefined : serverMessage,
        code,
      )
    case 'rate_limit_exceeded':
      return new ApiError(
        'RateLimitExceeded',
        mapErrorCode.RateLimitExceeded,
        status,
        undefined,
        code,
      )
    case 'user_not_found':
    case 'not_found':
      return new ApiError(
        'NotFound',
        mapErrorCode.NotFound,
        status,
        undefined,
        code,
      )
    case 'authentication_required':
    case 'token_expired':
    case 'invalid_token':
      return new ApiError(
        'Unauthorized',
        mapErrorCode.Unauthorized,
        status,
        undefined,
        code,
      )
    case 'beta_access_required':
      return new ApiError(
        'BetaAccessRequired',
        mapErrorCode.BetaAccessRequired,
        status,
        undefined,
        code,
      )
    default:
      if (code !== '') onUnmapped?.(code)
      // Keep the server's reason as a diagnostic detail. Non-JSON 4xx bodies
      // (e.g. axum's "Failed to deserialize the JSON body … unknown variant
      // `upscaling`") are the only actionable signal we get here — don't discard.
      return fromStatus(
        status,
        rawDetail(serverMessage, bodyText),
        code || undefined,
      )
  }
}

/** Prefer a JSON `message`, else a short slice of the raw (non-JSON) body. */
function rawDetail(
  serverMessage: string,
  bodyText: string,
): string | undefined {
  const raw = serverMessage || bodyText.trim()
  if (raw === '') return undefined
  return raw.length > 300 ? `${raw.slice(0, 300)}…` : raw
}
